import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
    tryToDeleteSuppliers,
    tryToEnableDisableSuppliers,
    tryToFetchSuppliers
} from '../../../store/brokers/admin/suppliers/SuppliersSlice';
import { IToast } from '../../../interfaces/components/IToast';
import { ISuppliers } from '../../../interfaces/suppliers/ISuppliers';
import { IPaginationPayload } from '../../../interfaces/shared/IPaginationPayload';
import SuppliersTable from './tables/SuppliersTable';
import SuppliersFilters from './details/SuppliersFilters';
import SearchWithButton from '../../../shared/search-with-button';
import DeleteModal from '../../../shared/delete-modal';
import Button from '../../../shared/button';
import Toast from '../../../shared/toast';


const Suppliers = () => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch();
    const [rows, setRows] = useState<ISuppliers[]>([]);
    const [showToast, setShowToast] = useState<IToast | null>()
    const [paginationState, setPaginationState] = useState<IPaginationPayload>({
        pageNumber: 1,
        pageSize: 10,
    });
    const [showDeleteSupplierModal, setShowDeleteSupplierModal] = useState<{ show: boolean, id: string }>()
    const [selectedEnabled, setSelectedEnabled] = useState<any>()
    const [search, setSearch] = useState<string>()
    const paginationData = useAppSelector((state) => state.suppliers.suppliers?.data?.page);
    const rowsLoading = useAppSelector((state) => state.suppliers.suppliersAreLoading);
    const state = useAppSelector((state) => state.suppliers);

    useEffect(() => {
        if (state.suppliers) {
            const rows =
                state.suppliers?.data?.elements && Array.isArray(state.suppliers?.data.elements)
                    ? state.suppliers?.data.elements.map((r) => ({ ...r, id: r._id }))
                    : [];
            setRows(rows);
        }
    }, [state.suppliers]);

    const onGettingFilters = (type?: string) => {
        let settingsFilters: any
        if (!type || type !== 'download') {
            settingsFilters = {
                ...paginationState
            }
        }
        if (selectedEnabled?.value) {
            const value = selectedEnabled?.value === 'Yes'
            settingsFilters.data = {
                ...settingsFilters.data,
                enabled: value
            }
        }
        if (search) {
            settingsFilters.data = {
                ...settingsFilters.data,
                search: search
            }
        }
        return settingsFilters
    }

    const onResetPaginationState = () => {
        setPaginationState({
            pageNumber: 1,
            pageSize: paginationState?.pageSize || 10,
        })
    }

    const getSuppliersData = async () => {
        const settingsFilters = onGettingFilters()
        dispatch(tryToFetchSuppliers(settingsFilters));
    }

    useEffect(() => {
        getSuppliersData();
    }, [paginationState]);

    const onAddNewSupplier = () => {
        navigate('/suppliers/supplier/create');
    }

    const onSeeSupplierDetails = async (id: string) => {
        navigate(`/suppliers/supplier/${id}`);
    }

    const onEnableDisableSupplier = async (id: string, currentStatus: boolean) => {
        try {
            await dispatch(tryToEnableDisableSuppliers({ id, status: !currentStatus })).unwrap();
            setShowToast({ type: 'success', message: `Supplier status changed successfully` });
        } catch (error) {
            setShowToast({ type: 'error', message: `${error}` });
        }
    }

    const onEditSupplier = (supplierId: string) => {
        navigate(`/suppliers/supplier/edit/${supplierId}`)
    }

    const onChangeSelectedBooleanItems = (value: string, name?: string) => {
        switch (name) {
            case 'enabled':
                setSelectedEnabled(value)
                break;
        }
        onResetPaginationState()
    }

    const onChangeSearchValue = (value: string | null | undefined, type: string) => {
        setSearch(value || undefined)
        onResetPaginationState()
    }

    const clearAllFilters = () => {
        setSelectedEnabled(undefined)
        setSearch(undefined)
        onResetPaginationState()
    }

    const onDeleteSupplierRequest = (id: string) => {
        setShowDeleteSupplierModal({ show: true, id: id })
    }

    const onDeleteSupplier = async () => {
        try {
            await dispatch(tryToDeleteSuppliers(showDeleteSupplierModal?.id || '')).unwrap()
            setShowToast({ type: 'success', message: `Supplier deleted successfully` })
        } catch (err) {
            setShowToast({ type: 'error', message: `${err}` })
        }
        setShowDeleteSupplierModal(undefined)
    }

    return (
        <div>
            <div>
                <SuppliersFilters
                    selectedEnabled={selectedEnabled}
                    onChangeSelectedBooleanItems={onChangeSelectedBooleanItems}
                />
            </div>
            <div className='my-7 flex flex-row justify-end min-w-[100%]'>
                    <Button
                        label={'Add New Supplier'}
                        onClickButton={onAddNewSupplier}
                        className={'btn-primary !col-span-2'}
                    />
            </div>
            <div>
                <SearchWithButton
                    placeholder='Filter suppliers'
                    search={search}
                    mainContainerStyle={'!grid-cols-10'}
                    onChangeSearchValue={onChangeSearchValue}
                    buttonLabel={'Clear All Filters'}
                    onClickButton={clearAllFilters}
                    buttonStyle={'btn-primary-text-underline min-w-auto col-span-2 flex flex-row justify-end bg-transparent mt-1'}
                />
            </div>
            <div className='my-5 relative'>
                <SuppliersTable
                    rows={rows}
                    rowsLoading={rowsLoading}
                    paginationData={paginationData}
                    onSeeSupplierDetails={onSeeSupplierDetails}
                    onEnableDisableSupplier={onEnableDisableSupplier}
                    setPaginationState={setPaginationState}
                    onEditSupplier={onEditSupplier}
                    onDeleteSupplierRequest={onDeleteSupplierRequest}
                />
            </div>
            {showDeleteSupplierModal?.show &&
                <DeleteModal
                    openDeleteModal={showDeleteSupplierModal?.show}
                    onApproveDeletion={onDeleteSupplier}
                    handleCloseDeleteModal={() => setShowDeleteSupplierModal(undefined)}
                />
            }
            {showToast?.message &&
                <Toast
                    type={showToast?.type}
                    text={showToast?.message}
                    onHandleCloseToast={() => setShowToast(null)}
                />
            }

        </div>
    )
}

export default Suppliers;